import { create } from "zustand";
import { PersistOptions, createJSONStorage, persist } from "zustand/middleware";

export type HeaderStore = {
  jamTitle: string;
  setJamTitle: (objective: string) => void;
  isUpgradeDialogOpen: boolean;
  setIsUpgradeDialogOpen: (isOpen: boolean) => void;
  isSettingsDialogOpen: boolean;
  setIsSettingsDialogOpen: (isOpen: boolean) => void;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (isOpen: boolean) => void;
  isGetExtraDialogOpen: boolean;
  setIsGetExtraDialogOpen: (isOpen: boolean) => void;
};

export const useHeaderStore = create<HeaderStore>((set) => ({
  jamTitle: "",
  setJamTitle: (title: string) => set({ jamTitle: title }),
  isUpgradeDialogOpen: false,
  setIsUpgradeDialogOpen: (isOpen: boolean) =>
    set({ isUpgradeDialogOpen: isOpen }),
  isSettingsDialogOpen: false,
  setIsSettingsDialogOpen: (isOpen: boolean) =>
    set({ isSettingsDialogOpen: isOpen }),
  isDropdownOpen: false,
  setIsDropdownOpen: (isOpen: boolean) => set({ isDropdownOpen: isOpen }),
  isGetExtraDialogOpen: false,
  setIsGetExtraDialogOpen: (isOpen: boolean) =>
    set({ isGetExtraDialogOpen: isOpen }),
}));

export type CanvasStore = {
  isCanvasOpen: boolean;
  setIsCanvasOpen: (isOpen: boolean) => void;
  activeTab: "timeline" | "files" | "app";
  setActiveTab: (tab: "timeline" | "files" | "app") => void;
  activeAssistantId: string | null;
  setActiveAssistantId: (id: string | null) => void;
  activeAppUrl: string | null;
  setActiveAppUrl: (url: string | null) => void;
  currentJamId: string | null;
  setCurrentJamId: (id: string | null) => void;
  selectedFileId: string | null;
  setSelectedFileId: (id: string | null) => void;
};

export const useCanvasStore = create<CanvasStore>((set) => ({
  isCanvasOpen: false,
  setIsCanvasOpen: (isOpen: boolean) => set({ isCanvasOpen: isOpen }),
  activeTab: "timeline" as "timeline" | "files" | "app",
  setActiveTab: (tab) => set({ activeTab: tab }),
  activeAssistantId: null,
  setActiveAssistantId: (id: string | null) => set({ activeAssistantId: id }),
  activeAppUrl: null,
  setActiveAppUrl: (url: string | null) => set({ activeAppUrl: url }),
  currentJamId: null,
  setCurrentJamId: (id: string | null) => set({ currentJamId: id }),
  selectedFileId: null,
  setSelectedFileId: (id: string | null) => set({ selectedFileId: id }),
}));

export type RocketTransitionStore = {
  isTransitioning: boolean;
  shouldShowStream: boolean;
  targetPath: string;
  triggerTransition: (targetPath: string) => void;
  resetTransition: () => void;
};

export const useRocketTransitionStore = create<RocketTransitionStore>(
  (set) => ({
    isTransitioning: false,
    shouldShowStream: true,
    targetPath: "",
    triggerTransition: (targetPath: string) => {
      set({ isTransitioning: true, shouldShowStream: false, targetPath });
      setTimeout(() => {
        set({ shouldShowStream: true });
      }, 1400);
    },
    resetTransition: () => set({ isTransitioning: false, targetPath: "" }),
  }),
);

export type DraftPromptStore = {
  draftPrompt: string;
  setDraftPrompt: (prompt: string) => void;
  clearDraftPrompt: () => void;
};

type DraftPromptPersist = PersistOptions<
  DraftPromptStore,
  Pick<DraftPromptStore, "draftPrompt">
>;

const draftPromptPersistConfig: DraftPromptPersist = {
  name: "draft-prompt-storage",
  storage: createJSONStorage(() => localStorage),
};

export const useDraftPromptStore = create<DraftPromptStore>()(
  persist(
    (set) => ({
      draftPrompt: "",
      setDraftPrompt: (prompt) => set({ draftPrompt: prompt }),
      clearDraftPrompt: () => set({ draftPrompt: "" }),
    }),
    draftPromptPersistConfig,
  ),
);

export type StatusStore = {
  isMaintenanceMode: boolean;
  setIsMaintenanceMode: (isActive: boolean) => void;
  isHighDemand: boolean;
  setIsHighDemand: (isActive: boolean) => void;
};

export const useStatusStore = create<StatusStore>((set) => ({
  isMaintenanceMode: false,
  setIsMaintenanceMode: (isActive: boolean) =>
    set({ isMaintenanceMode: isActive }),
  isHighDemand: false,
  setIsHighDemand: (isActive: boolean) => set({ isHighDemand: isActive }),
}));
